/**
 * Community Join Button Component
 * Handles join/leave actions with different states
 */

import { Clock, Loader2, UserCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/language"
import { useCommunityJoinActions, useCommunityMembership } from "../hooks/useCommunityMembership"

interface CommunityJoinButtonProps {
  communityId: string
  variant?: "default" | "outline" | "ghost" | "primary" | "secondary"
  size?: "default" | "sm" | "lg"
  className?: string
  initialStatus?: { isJoined: boolean; isPending: boolean }
  isOwner?: boolean
}

export function CommunityJoinButton({
  communityId,
  variant = "outline",
  size = "sm",
  className = "",
  initialStatus,
  isOwner = false,
}: CommunityJoinButtonProps) {
  const { t } = useTranslation("communities")

  // Skip fetching if initial status is provided
  const useQueryResult = useCommunityMembership(communityId, !!initialStatus)

  const isJoined = initialStatus?.isJoined ?? useQueryResult.isJoined
  const isPending = initialStatus?.isPending ?? useQueryResult.isPending
  const isLoading = initialStatus ? false : useQueryResult.isLoading
  const { join, leave, cancelRequest, isJoining, isLeaving, isCancelling } =
    useCommunityJoinActions()

  const isMutating = isJoining || isLeaving || isCancelling

  const handleAction = () => {
    if (isJoined) {
      leave(communityId)
    } else if (isPending) {
      cancelRequest(communityId)
    } else {
      join(communityId)
    }
  }

  // Don't show button for community creator
  if (isOwner) {
    return null
  }

  if (isLoading) {
    return (
      <Button variant={variant} size={size} className={className} disabled>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      </Button>
    )
  }

  if (isJoined) {
    return (
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleAction}
        disabled={isMutating}
      >
        {isMutating ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <>
            <UserCheck className="w-3.5 h-3.5" />
            <span>{t("joinButton.joined")}</span>
          </>
        )}
      </Button>
    )
  }

  if (isPending) {
    return (
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleAction}
        disabled={isMutating}
      >
        {isMutating ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <>
            <Clock className="w-3.5 h-3.5" />
            <span>{t("joinButton.requested")}</span>
          </>
        )}
      </Button>
    )
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleAction}
      disabled={isMutating}
    >
      {isMutating ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <span>{t("joinButton.join")}</span>
      )}
    </Button>
  )
}
